import { useState, FC } from 'react';
import DatePickerComponent from 'components/common/date-picker';
import InputField from 'components/common/input-field';
import PhoneInputComponent from 'components/common/phone-input';
import Select from 'components/common/select';
import { usersRoles } from 'helpers/utils';
import { useAppSelector } from 'hooks/useAppDispatchSelector';
import { onlyNumbers, countryList, stateList, cityList } from 'helpers/utils';
import { IDMSUserDetails, IDMSUserDetailsError } from '.';
import { useTranslation } from 'react-i18next';

const UsersDetails: FC<{
  user: IDMSUserDetails;
  setUser: (data: IDMSUserDetails) => void;
  userError: IDMSUserDetailsError | null;
}> = ({ user, setUser, userError }) => {
  const { selectedOrganization } = useAppSelector(state => state.auth);

  const { t } = useTranslation();
  const [dropdownSearch, setDropdownSearch] = useState<string>('');

  const isFleetManager = selectedOrganization?.role_name === usersRoles?.FLEET_MANAGE;

  const handleChange = (field: string, value: string, subField: string = '') => {
    let updatedData = { ...user };
    switch (field) {
      case 'address':
      case 'billing_address': {
        updatedData = { ...updatedData, [field]: { ...updatedData[field], [subField]: value } };
        break;
      }
      default: {
        updatedData = { ...updatedData, [field]: value };
        break;
      }
    }
    setUser(updatedData);
  };
  const getStateList = (key: 'address' | 'billing_address') => {
    const selectedCountry = countryList.find(
      (data: { name: string }) => data.name === user[key]?.country,
    );
    return stateList(selectedCountry?.isoCode) || [];
  };
  const getSelectedState = (key: 'address' | 'billing_address') => {
    const selectedCountry = countryList.find(
      (data: { name: string }) => data.name === user[key]?.country,
    );
    return (
      stateList(selectedCountry?.isoCode).find(
        (data: { name: string }) => data.name === user[key]?.state,
      ) || null
    );
  };
  const getCityList = (key: 'address' | 'billing_address') => {
    const selectedState = getSelectedState(key);
    return cityList(selectedState?.countryCode, selectedState?.isoCode) || [];
  };
  const getSelectedCity = (key: 'address' | 'billing_address') => {
    const selectedState = getSelectedState(key);
    return (
      cityList(selectedState?.countryCode, selectedState?.isoCode).find(
        (data: { name: string }) => data.name === user[key]?.city,
      ) || null
    );
  };
  const handleMobileNumber = (data: { code: string; number: string; format: string }) => {
    setUser({ ...user, mobile_no: data.number, country_code: data.code });
  };
  return (
    <div className="flex flex-wrap w-full h-full overflow-y-auto items-center">
      <div className="mb-3 px-3 w-1/4">
        <label className="font-medium text-sm text-black w-full">
          {t('administrator_roles.tableHeaders.name')}
          <span className="text-red-500 ml-1 text-base font-bold">*</span>
        </label>
        <InputField
          placeholder="Enter name"
          value={user?.name}
          onChange={val => setUser({ ...user, name: val })}
          error={userError?.name || ''}
        />
      </div>
      <div className="mb-3 px-3 w-1/4">
        <label className="font-medium text-sm text-black w-full">
          {t('administrator_roles.tableHeaders.last_name')}
          <span className="text-red-500 ml-1 text-base font-bold">*</span>
        </label>
        <InputField
          placeholder="Enter surname"
          value={user?.surname}
          onChange={val => setUser({ ...user, surname: val })}
          error={userError?.surname || ''}
        />
      </div>
      <div className="mb-3 px-3 w-1/2">
        <label className="font-medium text-sm text-black w-full">
          {t('administrator_roles.tableHeaders.emai')}
          <span className="text-red-500 ml-1 text-base font-bold">*</span>
        </label>
        <InputField
          placeholder="Enter email"
          value={user?.email}
          onChange={val => setUser({ ...user, email: val })}
          error={userError?.email || ''}
        />
      </div>
      <div className="mb-3 px-3 w-1/2">
        <label className="font-medium text-sm text-black w-full">
          {t('satisfactionSurvey.tableHeaders.telephone')}
          <span className="text-red-500 ml-1 text-base font-bold">*</span>
        </label>
        <PhoneInputComponent
          value={user?.mobile_no}
          countryCode={user?.country_code}
          onChange={data => handleMobileNumber(data)}
          error={userError?.mobile_number || ''}
        />
      </div>
      <div className="mb-3 px-3 w-1/2">
        <label className="font-medium text-sm text-black w-full">{t('users.dateOfBirth')}</label>
        <DatePickerComponent
          selected={user?.date_of_birth}
          onChange={date => setUser({ ...user, date_of_birth: date ? String(date) : '' })}
        />
      </div>
      <div className="w-1/2 px-3">
        <div className="flex flex-wrap -mx-3">
          <div className="w-full px-3 mt-1">
            <label className="font-bold text-base text-black2">{t('users.DNIDetails')}</label>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('users.dniNo')}</label>
              <InputField
                placeholder={t('users.eightDigitNo')}
                value={user?.dni_no || ''}
                onChange={val => setUser({ ...user, dni_no: val })}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('users.expDate')}</label>
              <DatePickerComponent
                placement="BOTTOM_RIGHT"
                selected={user?.dni_exp_date || ''}
                onChange={date => setUser({ ...user, dni_exp_date: String(date) })}
                isAllowPastDate={false}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="w-1/2 px-3">
        <div className="flex flex-wrap -mx-3">
          <div className="w-full px-3 mt-1">
            <label className="font-bold text-base text-black2">
              {t('users.drivingLicenceDetails')}
            </label>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">
                {t('users.licenseNo')}
                <span className="text-red-500 ml-1 text-base font-bold">*</span>
              </label>
              <InputField
                placeholder={t('users.enterLicenceNo')}
                value={user?.license_no}
                onChange={date => setUser({ ...user, license_no: String(date) })}
                error={userError?.license_no}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('users.expDate')}</label>
              <DatePickerComponent
                placement="BOTTOM_RIGHT"
                selected={user?.license_exp_date}
                onChange={date => setUser({ ...user, license_exp_date: String(date) })}
                isAllowPastDate={false}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="w-1/2 px-3">
        <div className="flex flex-wrap -mx-3">
          <div className="w-full px-3 mt-1">
            <label className="font-bold text-base text-black2">{t('users.insuranceDetails')}</label>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('users.policyNo')}</label>
              <InputField
                placeholder="Enter policy No."
                value={user?.policy_no || ''}
                onChange={val => setUser({ ...user, policy_no: val })}
              />
            </div>
          </div>
          <div className="w-1/2 px-3">
            <div className="mb-3">
              <label className="font-medium text-sm text-black w-full">{t('users.expDate')}</label>
              <DatePickerComponent
                placement="BOTTOM_RIGHT"
                selected={user?.policy_exp_date}
                onChange={date => setUser({ ...user, policy_exp_date: String(date) })}
                isAllowPastDate={false}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="w-1/2 px-3 mt-7">
        <div className="mb-3">
          <label className="font-medium text-sm text-black w-full">
            {t('users.typeOfPurchase')}
          </label>
          <Select
            placeholder={t('users.selectPurchaseType')}
            options={[]}
            selected={null}
            setSelected={paymentOpt => {}}
          />
        </div>
      </div>

      <div className="w-full px-3">
        <label className="font-medium text-sm text-black w-full">{'users.address'}</label>
      </div>
      <div className="w-1/2 px-3">
        <div className="mb-2">
          <InputField
            placeholder={t('ADDRESS1')}
            value={user?.address?.address1 || ''}
            onChange={val => handleChange('address', val, 'address1')}
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/2 px-3">
        <div className="mb-2">
          <InputField
            placeholder={t('ADDRESS2')}
            value={user?.address?.address2 || ''}
            onChange={val => handleChange('address', val, 'address2')}
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/4 px-3">
        <div className="mb-2">
          <Select
            placeholder={t('users.country')}
            selected={
              countryList.find((data: { name: string }) => data.name === user.address?.country) ||
              null
            }
            setSelected={data => handleChange('address', data?.name || '', 'country')}
            options={countryList.filter(
              coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
            )}
            isSearchable
            search={dropdownSearch}
            removeSearchOnBlur
            setSearch={val => setDropdownSearch(val)}
            dropdownPlacement="TOP"
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/4 px-3">
        <div className="mb-2">
          <Select
            placeholder={t('users.state')}
            selected={getSelectedState('address')}
            setSelected={data => handleChange('address', data?.name || '', 'state')}
            options={getStateList('address').filter(
              state => state?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
            )}
            isSearchable
            search={dropdownSearch}
            removeSearchOnBlur
            setSearch={val => setDropdownSearch(val)}
            dropdownPlacement="TOP"
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/4 px-3">
        <div className="mb-2">
          <Select
            placeholder={t('users.city')}
            selected={getSelectedCity('address')}
            setSelected={data => handleChange('address', data?.name || '', 'city')}
            options={getCityList('address').filter(
              cit => cit?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
            )}
            isSearchable
            search={dropdownSearch}
            removeSearchOnBlur
            setSearch={val => setDropdownSearch(val)}
            dropdownPlacement="TOP"
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/4 px-3">
        <div className="mb-2">
          <InputField
            placeholder={t('users.postalCode')}
            value={user.address?.zipcode || ''}
            onChange={val => {
              if (val && !onlyNumbers.test(val)) {
                return;
              }
              handleChange('address', val, 'zipcode');
            }}
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-full px-3">
        <label className="font-medium text-sm text-black w-full">{t('users.billingAddress')}</label>
      </div>
      <div className="w-1/2 px-3">
        <div className="mb-2">
          <InputField
            placeholder={t('ADDRESS1')}
            value={user?.billing_address?.address1 || ''}
            onChange={val => handleChange('billing_address', val, 'address1')}
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/2 px-3">
        <div className="mb-2">
          <InputField
            placeholder={t('ADDRESS2')}
            value={user?.billing_address?.address2 || ''}
            onChange={val => handleChange('billing_address', val, 'address2')}
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/4 px-3">
        <div className="mb-2">
          <Select
            placeholder={t('users.country')}
            selected={
              countryList.find(
                (data: { name: string }) => data.name === user.billing_address?.country,
              ) || { id: '00', name: user.billing_address?.country || '' }
            }
            setSelected={data => handleChange('billing_address', data?.name || '', 'country')}
            options={countryList.filter(
              coun => coun?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
            )}
            isSearchable
            search={dropdownSearch}
            setSearch={val => {
              setDropdownSearch(val);
              handleChange('billing_address', val, 'country');
            }}
            dropdownPlacement="TOP"
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/4 px-3">
        <div className="mb-2">
          <Select
            placeholder={t('users.state')}
            selected={
              getSelectedState('billing_address') || {
                id: '00',
                name: user.billing_address?.state || '',
              }
            }
            setSelected={data => handleChange('billing_address', data?.name || '', 'state')}
            options={getStateList('billing_address').filter(
              state => state?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
            )}
            isSearchable
            search={dropdownSearch}
            setSearch={val => {
              setDropdownSearch(val);
              handleChange('billing_address', val, 'state');
            }}
            dropdownPlacement="TOP"
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/4 px-3">
        <div className="mb-2">
          <Select
            placeholder={t('users.city')}
            selected={
              getSelectedCity('billing_address') || {
                id: '00',
                name: user.billing_address?.city || '',
              }
            }
            setSelected={data => handleChange('billing_address', data?.name || '', 'city')}
            options={getCityList('billing_address').filter(
              cit => cit?.name.toLowerCase().startsWith(dropdownSearch.toLowerCase()),
            )}
            isSearchable
            search={dropdownSearch}
            setSearch={val => {
              setDropdownSearch(val);
              handleChange('billing_address', val, 'city');
            }}
            dropdownPlacement="TOP"
            disabled={isFleetManager}
          />
        </div>
      </div>
      <div className="w-1/4 px-3">
        <div className="mb-2">
          <InputField
            placeholder={t('users.postalCode')}
            value={user.billing_address?.zipcode || ''}
            onChange={val => {
              if (val && !onlyNumbers.test(val)) {
                return;
              }
              handleChange('billing_address', val, 'zipcode');
            }}
            disabled={isFleetManager}
          />
        </div>
      </div>
    </div>
  );
};

export default UsersDetails;
